import * as Yup from 'yup';
import { isValid } from 'date-fns';
import _ from 'lodash';
import { WizardFormValues } from 'src/api/types/wizard';
import { WizardIntent, WizzardWorkspaceType } from 'src/redux/reducers/app/types';
import { FormikErrors } from 'formik';
import { Variable } from 'src/api/types';
import { QuestionsInput } from 'src/graphql/_generated';
import { apolloClient } from 'src/graphql/client';
import { UPSERT_WORKSPACE_VARIABLES } from 'src/graphql/schemas/mutations/org';
import { transformVariablesToGqlInput } from 'src/components/formComposed/WorkspaceVariablesForm/util';

export const SLIDE_HEIGHT = 400;

const positiveError = 'Value must be greater than 0';

export const initialValues: WizardFormValues = {
  organisation: {
    sector: undefined,
    annualRevenue: undefined, //variable
    numberOfStaff: undefined, //variable
    annualWageBill: undefined, //variable
    avgWorkingDays: 260, //variable
    annualCyberSpend: undefined, //variable
    avgStaffHardwareCost: undefined, //variable
    usesManagedServiceProvider: false,
  },
  customers: {
    numberOfCustomers: undefined, //variable
    avgContractValue: undefined, //variable
    numberOfDataRecords: undefined, //variable
  },
  softwareDev: {
    developsSoftware: false,
    releaseCycleCost: undefined, //variable
    releaseCycleLength: undefined, //variable
    researchAndDevelopmentBudget: undefined, //variable
  },
  supplyChain: {
    avgCostToProcureSupplier: undefined, //variable
    avgInvoiceValue: undefined, //variable
    largestRegularInvoiceValue: undefined, //variable
  },
  cyberInsurance: {
    cyberInsurance: false,
    cyberInsuranceLimit: undefined, //variable
    cyberInsuranceExcess: undefined, //variable
    cyberInsurancePremium: undefined, //variable
  },
  regulations: {
    legislation: [],
    controlFrameworks: [],
    controlFrameworksOther: [],
    primaryControlFramework: undefined,
    primaryFrameworkControls: [],
  },
  scenarios: {
    B2B: false,
    B2C: false,
    publiclyAccessibleInternetBusiness: false,
    paymentVolumeOrValue: false,
    significantITContributionToRevenue: false,
    significantDataInCloud: false,
    softwareBusiness: false,
    cniOrDefenseInvolvement: false,
    significantRandD: false,
    regulated: false,
    operationalTechnology: false,
    supplyChainReliant: false,
    aiThirdPartyReliant: false,
    aiCustomerFacingApp: false,
  },
  assessment: {
    name: null,
    description: null,
    started_date: null,
    review_date: null,
  },
};

export const validationSchema = (requireAssessment: boolean) => {
  return Yup.object().shape({
    organisation: Yup.object().shape({
      sector: Yup.string().nullable(),
      annualRevenue: Yup.number().nullable().positive(positiveError), //variable
      numberOfStaff: Yup.number().nullable().positive(positiveError), //variable
      annualWageBill: Yup.number().nullable().positive(positiveError), //variable
      avgWorkingDays: Yup.number().nullable().positive(positiveError), //variable
      annualCyberSpend: Yup.number().nullable().positive(positiveError), //variable
      avgStaffHardwareCost: Yup.number().nullable().positive(positiveError), //variable
      usesManagedServiceProvider: Yup.boolean(),
    }),
    customers: Yup.object().shape({
      numberOfCustomers: Yup.number().nullable().positive(positiveError), //variable
      avgContractValue: Yup.number().nullable().positive(positiveError), //variable
      numberOfDataRecords: Yup.number().nullable().positive(positiveError), //variable
    }),
    softwareDev: Yup.object().shape({
      developsSoftware: Yup.boolean(),
      releaseCycleCost: Yup.number().nullable().positive(positiveError), //variable
      releaseCycleLength: Yup.number().nullable().positive(positiveError), //variable
      researchAndDevelopmentBudget: Yup.number().nullable().positive(positiveError), //variable
    }),
    supplyChain: Yup.object().shape({
      avgCostToProcureSupplier: Yup.number().nullable().positive(positiveError), //variable
      avgInvoiceValue: Yup.number().nullable().positive(positiveError), //variable
      largestRegularInvoiceValue: Yup.number().nullable().positive(positiveError), //variable
    }),
    cyberInsurance: Yup.object().shape({
      cyberInsurance: Yup.boolean(),
      cyberInsuranceLimit: Yup.number().nullable().positive(positiveError), //variable
      cyberInsuranceExcess: Yup.number().nullable().positive(positiveError), //variable
      cyberInsurancePremium: Yup.number().nullable().positive(positiveError), //variable
    }),
    regulations: Yup.object().shape({
      legislation: Yup.array().of(Yup.string()).nullable(),
      controlFrameworks: Yup.array().of(Yup.string()).nullable(),
      controlFrameworksOther: Yup.array().of(Yup.string()).nullable(),
      primaryControlFramework: Yup.string().nullable(),
      primaryFrameworkControls: Yup.array().of(Yup.string()).nullable(),
    }),
    assessment: requireAssessment
      ? Yup.object().shape({
          name: Yup.string().required('Required'),
          review_date: Yup.string()
            .nullable()
            .test('review_date', 'Invalid Date', (date) => {
              if (!date) return true;
              if (typeof date === 'string') return isValid(new Date(date));
              return false;
            })
            .nullable(),
          description: Yup.string().nullable(),
        })
      : Yup.object(),
  });
};

type IsButtonDisabled = (params: {
  wizardWorkspace: WizzardWorkspaceType | null;
  isLastSlide: boolean;
  errors: FormikErrors<WizardFormValues>;
  isValid: boolean;
}) => boolean;

export const isButtonDisabled: IsButtonDisabled = ({ wizardWorkspace, isLastSlide, errors, isValid }) => {
  if (!isLastSlide) return false;
  if (wizardWorkspace?.intent.type === WizardIntent.workspace && !wizardWorkspace.intent.params.createAssessment) {
    return !_.isEmpty(_.omit(errors, 'assessment'));
  }
  return !isValid;
};

type NextButtonLabel = (params: {
  isLastSlide: boolean;
  wizardWorkspace: WizzardWorkspaceType | null;
  isStdVarComplete: boolean;
}) => string;

export const nextButtonLabel: NextButtonLabel = ({ isLastSlide, wizardWorkspace, isStdVarComplete }) => {
  if (!isLastSlide) return 'Save & Next';

  if (wizardWorkspace?.intent.type === WizardIntent.scenario) return '+ ADD';

  if (
    (wizardWorkspace?.intent.type === WizardIntent.workspace &&
      wizardWorkspace.intent.params.createAssessment &&
      !isStdVarComplete) ||
    wizardWorkspace?.intent.type === WizardIntent.assessment
  )
    return 'CREATE ASSESSMENT';

  return 'CONFIRM';
};

type UpdateVariablesWithFormData = (params: {
  formData: WizardFormValues;
  variables: Variable[] | null;
  onChanged: () => void;
  workspaceId: string;
}) => Promise<void>;
export const updateVariablesWithFormData: UpdateVariablesWithFormData = async ({
  formData,
  variables,
  onChanged,
  workspaceId,
}): Promise<void> => {
  if (!variables) return;

  const developsSoftware = _.get(formData, 'softwareDev.developsSoftware');
  const hasCyberInsurance = _.get(formData, 'cyberInsurance.cyberInsurance');

  const softwareDevVariableIDs = [500010, 500011, 500012];
  const cyberInsuranceVariableIDs = [500016, 500017, 500018];

  const updatedVariables = variables.map((variable) => {
    const formDataPath = variableIDToFormDataPath[variable.id];
    if (formDataPath) {
      if (
        (!developsSoftware && softwareDevVariableIDs.includes(variable.id)) ||
        (!hasCyberInsurance && cyberInsuranceVariableIDs.includes(variable.id))
      ) {
        return { ...variable, value: null };
      } else {
        const newValue = _.get(formData, formDataPath);
        if (true) {
          return { ...variable, value: newValue || null };
        }
      }
    }
    return variable;
  });

  const changedVariables = updatedVariables.filter((el, idx) => el.value !== variables[idx].value);

  if (changedVariables.length) {
    const variables = transformVariablesToGqlInput(changedVariables);

    await apolloClient.mutate({
      mutation: UPSERT_WORKSPACE_VARIABLES,
      variables: {
        workspaceId,
        variables: variables,
      },
    });

    onChanged();
  }
};

export const variableIDToFormDataPath: { [key: number]: string } = {
  500001: 'organisation.annualRevenue',
  500002: 'organisation.numberOfStaff',
  500003: 'organisation.annualWageBill',
  500004: 'organisation.avgWorkingDays',
  500005: 'organisation.annualCyberSpend',
  500006: 'organisation.avgStaffHardwareCost',
  500007: 'customers.numberOfCustomers',
  500008: 'customers.avgContractValue',
  500009: 'customers.numberOfDataRecords',
  500010: 'softwareDev.releaseCycleCost',
  500011: 'softwareDev.releaseCycleLength',
  500012: 'softwareDev.researchAndDevelopmentBudget',
  500013: 'supplyChain.avgCostToProcureSupplier',
  500014: 'supplyChain.avgInvoiceValue',
  500015: 'supplyChain.largestRegularInvoiceValue',
  500016: 'cyberInsurance.cyberInsuranceLimit',
  500017: 'cyberInsurance.cyberInsuranceExcess',
  500018: 'cyberInsurance.cyberInsurancePremium',
};

export const getVariableNameToIdMapping = (variables: Variable[]): Record<string, number> => {
  return variables.reduce((acc: Record<string, number>, variable) => {
    acc[variable.name] = variable.id;
    return acc;
  }, {});
};

export const getFormPathForVariableName = (variableName: string, variables: Variable[]): string | null => {
  const nameToIdMap = getVariableNameToIdMapping(variables);
  const id = nameToIdMap[variableName];

  if (id && variableIDToFormDataPath[id]) {
    return variableIDToFormDataPath[id];
  }

  return null;
};

export const formToQuestionsInputTransformer = (values: WizardFormValues): QuestionsInput => {
  return {
    // @ts-ignore
    scenarios: {
      B2B: values.scenarios.B2B,
      B2C: values.scenarios.B2C,
      regulated: values.scenarios.regulated,
      significantRandD: values.scenarios.significantRandD,
      softwareBusiness: values.scenarios.softwareBusiness,
      supplyChainReliant: values.scenarios.supplyChainReliant,
      aiThirdPartyReliant: values.scenarios.aiThirdPartyReliant,
      aiCustomerFacingApp: values.scenarios.aiCustomerFacingApp,
      paymentVolumeOrValue: values.scenarios.paymentVolumeOrValue,
      operationalTechnology: values.scenarios.operationalTechnology,
      significantDataInCloud: values.scenarios.significantDataInCloud,
      cniOrDefenseInvolvement: values.scenarios.cniOrDefenseInvolvement,
      publiclyAccessibleInternetBusiness: values.scenarios.publiclyAccessibleInternetBusiness,
      significantITContributionToRevenue: values.scenarios.significantITContributionToRevenue,
    },
    assessment: {
      name: values.assessment.name,
      description: values.assessment.description,
      review_date: values.assessment.review_date,
      started_date: values.assessment.started_date,
    },
    regulations: {
      legislation: values.regulations.legislation,
      controlFrameworks: values.regulations.controlFrameworks,
      controlFrameworksOther: values.regulations.controlFrameworksOther,
      primaryControlFramework: values.regulations.primaryControlFramework,
      primaryFrameworkControls: values.regulations.primaryFrameworkControls,
    },
    softwareDev: {
      developsSoftware: values.softwareDev.developsSoftware,
    },
    organisation: {
      sector: values.organisation.sector,
      usesManagedServiceProvider: values.organisation.usesManagedServiceProvider,
    },
    cyberInsurance: {
      cyberInsurance: values.cyberInsurance.cyberInsurance,
    },
  };
};
